import { IEnterAmountsDialogDatasetFilterSelections, IEnterAmountsDialogFiltersUIState } from '../hooks/useEnterAmountsData';
import { IAdjustment, ICalendarPeriod, ICase, IEntity, IJurisdiction } from '../model';

export enum EnterAmountsDialogFiltersActionType {
    AdjustmentChanged,
    ApplyButtonClicked,
    CalendarPeriodChanged,
    CaseChanged,
    EntityChanged,
    JurisdictionChanged,
    ResetButtonClicked,
}

export interface IEnterAmountsDialogFiltersActionBase {
    type: EnterAmountsDialogFiltersActionType;
}

export interface IEnterAmountsDialogFilterAdjustmentChangeAction
    extends IEnterAmountsDialogFilterFilterChangeAction<IAdjustment | undefined> {
    type: EnterAmountsDialogFiltersActionType.AdjustmentChanged;
}

export interface IEnterAmountsDialogFiltersApplyButtonClickAction extends IEnterAmountsDialogFiltersActionBase {
    type: EnterAmountsDialogFiltersActionType.ApplyButtonClicked;
}

export interface IEnterAmountsDialogFiltersResetButtonClickAction extends IEnterAmountsDialogFiltersActionBase {
    type: EnterAmountsDialogFiltersActionType.ResetButtonClicked;
    initialState: IEnterAmountsDialogFiltersUIState;
}

export interface IEnterAmountsDialogFilterCalendarPeriodChangeAction
    extends IEnterAmountsDialogFilterFilterChangeAction<ICalendarPeriod | undefined> {
    type: EnterAmountsDialogFiltersActionType.CalendarPeriodChanged;
}

export interface IEnterAmountsDialogFilterCaseChangeAction extends IEnterAmountsDialogFilterFilterChangeAction<ICase | undefined> {
    type: EnterAmountsDialogFiltersActionType.CaseChanged;
}

export interface IEnterAmountsDialogFilterFilterChangeAction<T> extends IEnterAmountsDialogFiltersActionBase {
    newFilterValue: T;
}

export interface IEnterAmountsDialogFilterEntityChangeAction extends IEnterAmountsDialogFilterFilterChangeAction<IEntity | undefined> {
    type: EnterAmountsDialogFiltersActionType.EntityChanged;
}

export interface IEnterAmountsDialogFilterJurisdictionChangeAction
    extends IEnterAmountsDialogFilterFilterChangeAction<IJurisdiction | undefined> {
    type: EnterAmountsDialogFiltersActionType.JurisdictionChanged;
}

export declare type EnterAmountsDialogFiltersFilterComboboxChangeActions =
    | IEnterAmountsDialogFilterAdjustmentChangeAction
    | IEnterAmountsDialogFilterCalendarPeriodChangeAction
    | IEnterAmountsDialogFilterCaseChangeAction
    | IEnterAmountsDialogFilterEntityChangeAction
    | IEnterAmountsDialogFilterJurisdictionChangeAction;

export declare type EnterAmountsDialogFiltersAction =
    | IEnterAmountsDialogFilterAdjustmentChangeAction
    | IEnterAmountsDialogFiltersApplyButtonClickAction
    | IEnterAmountsDialogFilterCalendarPeriodChangeAction
    | IEnterAmountsDialogFilterCaseChangeAction
    | IEnterAmountsDialogFilterEntityChangeAction
    | IEnterAmountsDialogFilterJurisdictionChangeAction
    | IEnterAmountsDialogFiltersResetButtonClickAction;

export interface IEnterAmountsDialogComboboxPropsBase<
    TOption,
    TAction extends IEnterAmountsDialogFilterFilterChangeAction<TOption | undefined> & EnterAmountsDialogFiltersFilterComboboxChangeActions
> {
    options: TOption[] | null | undefined;
    selectedOption: TOption | undefined;
    dispatch?: React.Dispatch<TAction>;
    clearable?: boolean;
}

export type EnterAmountsDialogFiltersReducer = (
    state: IEnterAmountsDialogFiltersUIState,
    action: EnterAmountsDialogFiltersAction
) => IEnterAmountsDialogFiltersUIState;

export const enterAmountsDialogFiltersReducer: EnterAmountsDialogFiltersReducer = (
    state: IEnterAmountsDialogFiltersUIState,
    action: EnterAmountsDialogFiltersAction
) => {
    switch (action.type) {
        case EnterAmountsDialogFiltersActionType.ApplyButtonClicked:
            return { ...state, currentFilterSelections: null, applyButtonDisabled: true };
        case EnterAmountsDialogFiltersActionType.ResetButtonClicked:
            return { ...action.initialState };
        case EnterAmountsDialogFiltersActionType.AdjustmentChanged:
        case EnterAmountsDialogFiltersActionType.CalendarPeriodChanged:
        case EnterAmountsDialogFiltersActionType.CaseChanged:
        case EnterAmountsDialogFiltersActionType.EntityChanged:
        case EnterAmountsDialogFiltersActionType.JurisdictionChanged:
            return {
                ...state,
                currentFilterSelections: {
                    ...state.currentFilterSelections,
                    [getFilterNameForActionType(action.type)]: action.newFilterValue,
                },
                applyButtonDisabled: false,
            };

        default:
            throw new Error('Invalid action type passed to enterAmountsDialogFiltersReducer');
    }
};

export function getFilterNameForActionType(
    actionType: EnterAmountsDialogFiltersActionType
): keyof IEnterAmountsDialogDatasetFilterSelections {
    switch (actionType) {
        case EnterAmountsDialogFiltersActionType.AdjustmentChanged:
            return 'adjustment';

        case EnterAmountsDialogFiltersActionType.CalendarPeriodChanged:
            return 'calendarPeriod';

        case EnterAmountsDialogFiltersActionType.CaseChanged:
            return 'case';

        case EnterAmountsDialogFiltersActionType.EntityChanged:
            return 'entity';

        case EnterAmountsDialogFiltersActionType.JurisdictionChanged:
            return 'jurisdiction';

        default:
            throw new Error(`Invalid action type passed to getFilterNameForActionType: ${actionType}`);
    }
}
